import * as proto from 'src/proto/compiled-protos';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { BackendService } from '../../services/backend.service';
import { CompactTextColumn, DataTableColumn, StringColumn } from 'src/app/general/components/data-table/data-table-column';
import { ComponentUtil } from 'src/app/general/util/component-util';
import { DataTableComponent } from 'src/app/general/components/data-table/data-table.component';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { ErrorResult, LoadingMessage } from 'src/app/general/util/result';
import { IndeterminatePaginatorComponent } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator.component';
import { IndeterminatePaginatorData, IndeterminatePaginatorModel } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator-model';
import { map, Observable, of } from 'rxjs';
import { Util } from 'src/app/general/util/util';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// <app-top-businesses year="{{year}}"></app-business-events>
// <app-top-businesses year="{{year}}" month="{{month}}"></app-business-events>
// <app-top-businesses year="{{year}}" month="{{month}}" day="{{day}}"></app-business-events>
// ----------------------------------------------------------------------------

@Component({
  selector: 'app-top-businesses',
  standalone: true,
  imports: [
    CommonModule,
    LoadableContentComponent,
    TranslateModule,
    MatCardModule,
    DataTableComponent,
    IndeterminatePaginatorComponent
  ],
  templateUrl: './top-businesses.component.html',
  styleUrls: ['./top-businesses.component.css']
})
export class TopBusinessesComponent implements IndeterminatePaginatorModel<proto.waiternow.common.IBusinessMetricProto>, OnInit, OnChanges {
  @Input() year!: number;
  @Input() month?: proto.waiternow.common.Month;
  @Input() day?: number;

  loadingController: LoadingController;

  topBusinesses: Array<proto.waiternow.common.IBusinessMetricProto>;
  columns: Array<DataTableColumn<proto.waiternow.common.IBusinessMetricProto>>;

  initialPaginatorData: IndeterminatePaginatorData<proto.waiternow.common.IBusinessMetricProto>;
  paginatorModel: IndeterminatePaginatorModel<proto.waiternow.common.IBusinessMetricProto>;

  constructor(
      private backendService: BackendService,
      private changeDetectorRef: ChangeDetectorRef) {
    this.loadingController = new LoadingController();

    this.topBusinesses = new Array();
    this.columns = [
      new CompactTextColumn(
        /* name= */ 'id',
        /* translateId= */ 'id',
        /* valueExtractor= */  businessMetric => businessMetric.businessId,
        /* compactTextLength= */ 6),
      new StringColumn(
        /* name= */ 'business',
        /* translateId= */ 'business',
        /* valueExtractor= */  businessMetric => Util.safeString(businessMetric.redundantData?.businessName)),
      new StringColumn(
        /* name= */ 'paidOrders',
        /* translateId= */ 'paid_orders',
        /* valueExtractor= */  businessMetric => Util.safeNumber(businessMetric.paidOrders).toString()),
      ];

      this.initialPaginatorData = { data: new Array(), continuationToken: ''};
      this.paginatorModel = this;
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (ComponentUtil.bindingChanged('year', changes)
        || ComponentUtil.bindingChanged('month', changes)
        || ComponentUtil.bindingChanged('day', changes)) {
      this.ngOnInit();
    }
  }

  updatePage(page: proto.waiternow.common.IBusinessMetricProto[]): void {
    this.topBusinesses = page;
    // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
    this.changeDetectorRef.detectChanges();
  }

  fetchData(continuationToken: string | null | undefined): Observable<IndeterminatePaginatorData<proto.waiternow.common.IBusinessMetricProto>> {
    if (!this.year && ! this.month && !this.day) {
      return of({data: [], continuationToken: ''});
    }

    return this.backendService.findTopBusinessesReturnObservable(this.year, this.month, this.day, continuationToken)
      .pipe(
        map(businessMetrics  => {
          const paginatorData: IndeterminatePaginatorData<proto.waiternow.common.IBusinessMetricProto> = {data: [], continuationToken: continuationToken};
          if (businessMetrics) {
            paginatorData.data = businessMetrics.metrics;
            paginatorData.continuationToken = businessMetrics.continuationToken;
          }
          return paginatorData;
        })
      );
  }

  public loadInitialData(): void {
    this.loadingController.onLoadingStarted(LoadingMessage.withMessageTranslateId('loading'));
    this.fetchData(/* continuationToken= */ null).subscribe(
      {
        next: paginatorData => {
          this.initialPaginatorData = paginatorData;
          this.loadingController.onSuccess();
          // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          this.loadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_loading'));
        }
      }
    );
  }

  public getDate(): string {
    if (this.year && this.month && this.day) {
      return `${this.year}-${this.pad(this.month)}-${this.pad(this.day)}`;
    } else if (this.year && this.month) {
      return `${this.year}-${this.pad(this.month)}`;
    } else if (this.year) {
      return `${this.year}`;
    }
    return '';
  }

  private pad(dateComponent: number): string {
    return dateComponent.toString().padStart(2, '0');
  }
}
